import React, { useEffect, useRef, useState } from 'react';
import ScreenWrapper from '../../components/ScreenWrapper/ScreenWrapper';
import { Linking, View, useWindowDimensions } from 'react-native';
import loadjs from 'loadjs';
import {
  AEM_API_BASE_URL,
  CAPTCHA_KEY,
  PNC_SHO_URL,
} from '../../config/getEnvironmentVars';
import { Components, LoadingOverlay } from '@oneamerica/dxp-ui-components';
import responsiveStyles from './style';
import moment from 'moment';
import { trackCustomEvent } from '../../utils/Analytics/AdobeAnalyticsUtils';
import { analyticsTags } from '../../utils/Analytics/AnalyticsTags';
import { NetworkManager } from '../../api/NetworkManager';
import { AlertLevel } from '../../constants/UtilsConstants';
import BreakpointConstants from '../../constants/BreakpointConstants';
import MakePaymentModal from '../PolicyDetails/components/BillingAndPremium/MakePaymentModal';
import LegalDisclaimerModal from '../PolicyDetails/components/BillingAndPremium/LegalDisclaimerModal';
import { ProcessingTroubleModal } from '../../components/ProcessingTroubleModal';

const DATEFORMAT = 'MM/DD/YYYY';
const getPaymentStatusKey = (policyNumber) =>
  `guestPaymentStatus_${policyNumber}`;

const handlePaymentStatusFromURL = (
  getPaymentStatusKey: (pn: string) => string,
  redirectShownRef: React.RefObject<boolean>,
  setPaymentStatus: (status: string | undefined) => void,
  props?: any,
) => {
  const urlParams = new URLSearchParams(globalThis.location.search);

  // Check both URL params and navigation route paramss
  const routeParams = props?.route?.params || {};
  const initialRedirectIsSuccess =
    urlParams.get('paymentStatus') === 'success' ||
    routeParams.paymentStatus === 'success';
  const isCancelRedirect =
    urlParams.get('paymentStatus') === 'cancel' ||
    routeParams.paymentStatus === 'cancel';
  // Get policy number from URL params, route params, or use 'guest' as fallback
  const policyNumber =
    urlParams.get('account') || routeParams.account || 'guest';

  const key = getPaymentStatusKey(policyNumber);
  const storedStatus = sessionStorage.getItem(key);

  // Only show banner on the very first visit with the success parameter for this policy number
  if (
    initialRedirectIsSuccess &&
    storedStatus !== 'handled' &&
    storedStatus !== 'closed'
  ) {
    // Mark as handled to prevent showing again for this specific policy number
    sessionStorage.setItem(key, 'handled');
    redirectShownRef.current = true;

    // Clean URL parameters
    try {
      const u = new URL(globalThis.location.href);
      u.searchParams.delete('paymentStatus');
      u.searchParams.delete('account');
      globalThis.history.replaceState(null, '', u.toString());
    } catch {}

    setPaymentStatus('success');
    return;
  } else if (isCancelRedirect) {
    sessionStorage.removeItem('pncCallbackRole');
  }

  // In all other cases (refresh, manual close, etc.), don't show banner
  setPaymentStatus(undefined);
};

const SuccessBanner = ({
  width,
  styles,
  phnLinkStyle,
  onClose,
}: {
  width: number;
  styles: any;
  phnLinkStyle: any;
  onClose: () => void;
}) => (
  <Components.AlertMessage
    className="mt-4"
    showCloseButton={true}
    onCloseMessage={onClose}
    alertLevel={AlertLevel.SUCCESS}
    iconSize={BreakpointConstants.isLG(width) ? 52.36 : 24}
    title="Thank you for your payment"
  >
    <Components.Text style={styles.marginTop16}>
      {`Call us at `}
      <Components.Link
        onPress={() => {
          Linking.openURL('tel:800-537-6442');
        }}
        accessibilityLabel="contact number"
        accessibilityRole="button"
        {...phnLinkStyle}
      >
        800-537-6442
      </Components.Link>
      {` to make any changes`}
    </Components.Text>
  </Components.AlertMessage>
);

const IneligiblePaymentBanner = ({
  width,
  styles,
  phnLinkStyle,
  onClose,
}: {
  width: number;
  styles: any;
  phnLinkStyle: any;
  onClose: () => void;
}) => (
  <Components.AlertMessage
    className="mt-4"
    showCloseButton={true}
    onCloseMessage={onClose}
    alertLevel={AlertLevel.ERROR}
    iconSize={BreakpointConstants.isLG(width) ? 52.36 : 24}
    title="This account is ineligible for online payments"
  >
    <Components.Text style={styles.alertBannerContentMargin}>
      {`Please `}
      <Components.Link
        onPress={() => {
          window.open(`${AEM_API_BASE_URL}/contact-us`, '_blank');
        }}
        accessibilityLabel="Contact Us"
        accessibilityRole="button"
        {...phnLinkStyle}
      >
        Contact Us
      </Components.Link>
      {` for further assistance`}
    </Components.Text>
  </Components.AlertMessage>
);

const GuestPayment = (props) => {
  const { width } = useWindowDimensions();
  const styles = responsiveStyles(width);

  const [isPendingBannerVisible, setIsPendingBannerVisible] =
    useState<boolean>(false);
  const [isErrorBannerVisible, setIsErrorBannerVisible] =
    useState<boolean>(false);
  const [isIneligibleBannerVisible, setIsIneligibleBannerVisible] =
    useState<boolean>(false);
  const [isProcessingTroublePopupVisible, setIsProcessingTroublePopupVisible] =
    useState<boolean>(false);

  const [guestTokenHeader, setGuestTokenHeader] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentEncryptedData, setPaymentEncryptedData] = useState<any>([]);
  const [modalResetKey, setModalResetKey] = useState(0);
  const [showLegalDisclaimer, setShowLegalDisclaimer] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();

  const formRef = useRef<HTMLFormElement | null>(null);

  const redirectShownRef = useRef(false);

  const [formData, setFormData] = useState({
    policyNumber: '',
    identificationType: 'ssn', // 'ssn' or 'ein'
    ssn: '',
    ein: '',
    dateOfBirth: '', // This will store YYYY-MM-DD for API
    email: '',
  });
  const [errors, setErrors] = useState({
    policyNumber: '',
    ssn: '',
    ein: '',
    dateOfBirth: '',
    email: '',
  });
  const [errorList, setErrorList] = useState<any[]>([]);

  const isFormComplete = (): boolean => {
    if (formData.policyNumber.trim() === '') return false;
    if (formData.email.trim() === '' || validateEmail(formData.email) === false)
      return false;
    if (formData.dateOfBirth.trim() === '') return false;

    // Check if there are any date validation errors
    if (errorList.length > 0) return false;

    if (formData.identificationType === 'ssn') {
      const unformattedSSN = unformatSSN(formData.ssn);
      if (unformattedSSN === '' || unformattedSSN.length !== 9) return false;
    } else {
      const unformattedEIN = unformatSSN(formData.ein);
      if (unformattedEIN === '' || unformattedEIN.length !== 9) return false;
    }

    return true;
  };

  const phnLinkStyle = {
    style: styles.phnlinkStyle,
    linkStyle: styles.phnlinkStyle,
    linkHoverStyle: styles.phnlinkStyle,
    hoverStyle: styles.phnlinkStyle,
    focusStyle: styles.phnlinkStyle,
  };

  // Clear form on page load/refresh
  const handleBeforeUnload = () => {
    setFormData({
      policyNumber: '',
      identificationType: 'ssn',
      ssn: '',
      ein: '',
      dateOfBirth: '',
      email: '',
    });
    setErrors({
      policyNumber: '',
      ssn: '',
      ein: '',
      dateOfBirth: '',
      email: '',
    });
    setErrorList([]);
  };

  useEffect(() => {
    loadjs(
      `https://www.google.com/recaptcha/enterprise.js?render=${CAPTCHA_KEY}`,
    );

    // Handle payment status from URL parameters
    handlePaymentStatusFromURL(
      getPaymentStatusKey,
      redirectShownRef,
      setPaymentStatus,
      props,
    );

    globalThis.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });

    // Validate on change
    const newErrors = { ...errors };

    if (field === 'policyNumber') {
      if (value.trim() === '') {
        newErrors.policyNumber = 'Policy Number is required';
      } else {
        newErrors.policyNumber = '';
      }
    }

    if (field === 'email') {
      if (value.trim() === '') {
        newErrors.email = 'Email address is required';
      } else if (validateEmail(value) === false) {
        newErrors.email = 'Please enter a valid email address';
      } else {
        newErrors.email = '';
      }
    }

    setErrors(newErrors);

    // Clear the opposite identification field when switching types
    if (field === 'identificationType') {
      if (value === 'ssn') {
        setFormData({ ...formData, identificationType: value, ein: '' });
        setErrors({ ...errors, ein: '' });
      } else if (value === 'ein') {
        setFormData({ ...formData, identificationType: value, ssn: '' });
        setErrors({ ...errors, ssn: '' });
      }
    }
  };

  const formatSSN = (value: string): string => {
    // Remove all non-numeric characters
    const numbers = value.replaceAll(/\D/g, '');
    // Limit to 9 digits and return without formatting
    return numbers.slice(0, 9);
  };

  // NOSONAR: intentionally duplicated implementation with formatSSN
  const formatEIN = (value: string): string => { //NOSONAR
    // Remove all non-numeric characters
    const numbers = value.replaceAll(/\D/g, '');
    // Limit to 9 digits and return without formatting
    return numbers.slice(0, 9);
  };

  const unformatSSN = (value: string): string => {
    return value.replaceAll(/\D/g, '');
  };

  const handleSSNInput = (value: string) => {
    const formatted = formatSSN(value);
    setFormData({ ...formData, ssn: formatted });

    // Validate SSN on change
    const newErrors = { ...errors };
    const unformattedSSN = unformatSSN(formatted);

    if (unformattedSSN === '') {
      newErrors.ssn = 'SSN is required';
    } else if (unformattedSSN.length === 9) {
      newErrors.ssn = '';
    } else {
      newErrors.ssn = 'Please enter 9 digit SSN or Tax ID';
    }

    setErrors(newErrors);
  };

  const handleEINInput = (value: string) => {
    const formatted = formatEIN(value);
    setFormData({ ...formData, ein: formatted });

    // Validate EIN on change
    const newErrors = { ...errors };
    const unformattedEIN = unformatSSN(formatted);

    if (unformattedEIN === '') {
      newErrors.ein = 'Federal EIN is required';
    } else if (unformattedEIN.length === 9) {
      newErrors.ein = '';
    } else {
      newErrors.ein = 'Please enter 9 digit EIN';
    }

    setErrors(newErrors);
  };

  const validateForm = (): boolean => {
    const newErrors = {
      policyNumber: '',
      ssn: '',
      ein: '',
      dateOfBirth: '',
      email: '',
    };
    let isValid = true;

    if (formData.policyNumber.trim() === '') {
      newErrors.policyNumber = 'Policy Number is required';
      isValid = false;
    }

    if (formData.identificationType === 'ssn') {
      const unformattedSSN = unformatSSN(formData.ssn);
      if (unformattedSSN === '') {
        newErrors.ssn = 'Social Security Number is required';
        isValid = false;
      } else if (unformattedSSN.length !== 9) {
        newErrors.ssn = 'Social Security Number must be 9 digits';
        isValid = false;
      }
    }

    if (formData.identificationType === 'ein') {
      const unformattedEIN = unformatSSN(formData.ein);
      if (unformattedEIN === '') {
        newErrors.ein = 'Federal EIN is required';
        isValid = false;
      } else if (unformattedEIN.length !== 9) {
        newErrors.ein = 'Federal EIN must be 9 digits';
        isValid = false;
      }
    }

    if (formData.dateOfBirth.trim() === '') {
      newErrors.dateOfBirth = 'Date of Birth is required';
      isValid = false;
    }

    if (formData.email.trim() === '') {
      newErrors.email = 'Email address is required';
      isValid = false;
    } else if (validateEmail(formData.email) === false) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCaptchaError = (e: any) => {
    if (isPendingBannerVisible !== false) {
      setIsPendingBannerVisible(false);
    }
    if (isErrorBannerVisible !== false) {
      setIsErrorBannerVisible(false);
    }
    if (isIneligibleBannerVisible !== false) {
      setIsIneligibleBannerVisible(false);
    }
    setIsLoading(false);
    console.log('reCAPTCHA error:', e);
  };

  const handlePaymentValidationError = (error: any) => {
    if (isPendingBannerVisible !== false) {
      setIsPendingBannerVisible(false);
    }
    if (isErrorBannerVisible !== false) {
      setIsErrorBannerVisible(false);
    }
    if (isIneligibleBannerVisible !== false) {
      setIsIneligibleBannerVisible(false);
    }
    if (
      error?.status === 400 ||
      error.data.errorInfo.code == 400 ||
      error?.status === 412 ||
      error.data.errorInfo.code == 412
    ) {
      setIsErrorBannerVisible(true);
    } else {
      setIsProcessingTroublePopupVisible(true);
    }

    setIsLoading(false);
    trackCustomEvent(analyticsTags.CaptureGuestPaymentLoginFailure);
    console.error('Error validating guest payment login:', error);
  };

  const handlePaymentValidationSuccess = (data: any, headers: any) => {
    setPaymentData(data);

    // Extract response headers
    const guestToken = headers['x-oa-guesttoken'];
    if (guestToken) {
      setGuestTokenHeader(guestToken);
    }

    setIsPendingBannerVisible(data.hasPendingPayment);
    if (isErrorBannerVisible !== false) {
      setIsErrorBannerVisible(false);
    }
    // Check if allowPayment is false
    if (data.allowPayment === false) {
      setIsIneligibleBannerVisible(true);
    } else {
      setIsIneligibleBannerVisible(false);
    }
    setPaymentStatus(undefined); // to hide any existing banners
    trackCustomEvent(analyticsTags.CaptureGuestPaymentLoginSuccess);
    setIsLoading(false);
    if (!data.hasPendingPayment && data.allowPayment) {
      setModalResetKey((prev) => prev + 1);
      setIsModalVisible(true);
    }
  };

  const executePaymentValidation = async (token: string) => {
    try {
      const payload = {
        taxID:
          formData.identificationType === 'ssn'
            ? unformatSSN(formData.ssn)
            : unformatSSN(formData.ein),
        taxIDType: formData.identificationType === 'ssn' ? 'SSN' : 'EIN',
        birthDate: formData.dateOfBirth,
        policyNumber: formData.policyNumber,
        email: formData.email,
        captchaResponse: token,
      };

      const response = await NetworkManager.validateGuestPaymentLogin({
        payload,
      });
      const { data, headers } = response;
      handlePaymentValidationSuccess(data, headers);
    } catch (error) {
      handlePaymentValidationError(error);
    }
  };

  const clearStorage = () => {
    if (sessionStorage.getItem('pncCallbackRole')) {
      sessionStorage.removeItem('pncCallbackRole');
    }

    // Remove all sessionStorage keys that start with 'guestPaymentStatus'
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('guestPaymentStatus')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  };

  const handleSubmit = () => {
    clearStorage();
    setPaymentStatus(undefined);
    setIsLoading(true);
    if (validateForm() === false) {
      return;
    }
    if (isPendingBannerVisible !== false) {
      setIsPendingBannerVisible(false);
    }
    if (isErrorBannerVisible !== false) {
      setIsErrorBannerVisible(false);
    }
    (globalThis as any).grecaptcha.enterprise.ready(() => {
      (globalThis as any).grecaptcha.enterprise
        .execute(CAPTCHA_KEY, {
          action: 'submit',
        })
        .then(executePaymentValidation)
        .catch(handleCaptchaError);
    });
  };

  const handleExit = () => {
    clearStorage();
    trackCustomEvent(analyticsTags.CaptureGuestPaymentLoginExit);
    (globalThis as any).location.href = AEM_API_BASE_URL;
  };

  const isDateValid = (date: string, format: string) => {
    // Convert from YYYY-MM-DD (stored) to MM/DD/YYYY (display)
    if (date && moment(date, 'YYYY-MM-DD', true).isValid()) {
      return moment(date, 'YYYY-MM-DD').format(DATEFORMAT);
    }
    return '';
  };

  const onChangeDate = (date: string | undefined) => {
    if (date === undefined || date === '') {
      setErrorList([]);
      setErrors({ ...errors, dateOfBirth: '' });
      handleInputChange('dateOfBirth', '');
      return;
    }

    // Strict MM/DD/YYYY check
    const mmddyyyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    // Only allow exactly 10 characters (MM/DD/YYYY)
    if (!mmddyyyyRegex.test(date) || date.length !== 10) {
      createErrorList('Please enter a valid date in MM/DD/YYYY format.');
      setErrors({ ...errors, dateOfBirth: '' });
      return;
    }

    // Check for valid calendar date
    if (!moment(date, DATEFORMAT, true).isValid()) {
      createErrorList('Please enter a valid calendar date.');
      setErrors({ ...errors, dateOfBirth: '' });
      return;
    }

    if (moment(date, DATEFORMAT).isAfter(moment())) {
      createErrorList('Date of Birth must be in the past.');
      setErrors({ ...errors, dateOfBirth: '' });
      return;
    }

    setErrorList([]);
    setErrors({ ...errors, dateOfBirth: '' });
    handleInputChange(
      'dateOfBirth',
      moment(date, DATEFORMAT).format('YYYY-MM-DD'),
    );
  };

  const createErrorList = (errorMessage: string) => {
    const errorListData = [{ errorItem: errorMessage }];
    setErrorList(errorListData);
  };

  const getPaymentTypes = () => {
    const payments: { title: string; amount: number }[] = [];

    if ((paymentData as any)?.billingAmount) {
      payments.push({
        title: 'Base Premium',
        amount: (paymentData as any)?.billingAmount,
      });
    } else {
      payments.push({
        title: 'Base Premium',
        amount: 0,
      });
    }

    if ((paymentData as any)?.billRiderAmt) {
      payments.push({
        title: 'Paid Up Addition',
        amount: (paymentData as any)?.billRiderAmt,
      });
    } else {
      payments.push({
        title: 'Paid Up Addition',
        amount: 0,
      });
    }

    const filteredPayments = payments.filter((item) => item.amount > 0);
    return filteredPayments;
  };

  const sendAnalytics = () => {
    trackCustomEvent(analyticsTags.CaptureGuestPaymentCloseButtonTags);
  };

  const getSelectedPaymentsCodes = (selectedPayments): string[] => {
    const hasBase = selectedPayments.some(
      (p) => p.title?.toLowerCase() === 'base premium',
    );
    const hasPuar = selectedPayments.some(
      (p) => p.title?.toLowerCase() === 'paid up addition',
    );
    return [hasBase && 'P', hasPuar && 'U'].filter(Boolean) as string[];
  };

  const handlePaymentSubmit = async (selectedPayments) => {
    setIsLoading(true);
    trackCustomEvent(analyticsTags.CaptureGuestPaymentSubmitButtonTags);

    try {
      const response = await NetworkManager.getGuestPaymentEncryptedData({
        guestToken: guestTokenHeader,
        paymentTypes: getSelectedPaymentsCodes(selectedPayments),
      });

      setPaymentEncryptedData(response.data);
      setIsLoading(false);
      // Close the premium modal
      setIsModalVisible(false);
      // Show the legal disclaimer modal
      setShowLegalDisclaimer(true);
    } catch (error) {
      console.error('Error fetching guest payment encrypted data:', error);
      trackCustomEvent(
        analyticsTags.CaptureGuestPaymentSubmitButtonFailureTags,
      );
      setIsLoading(false);
      setIsProcessingTroublePopupVisible(true);
    }
  };

  const handleLegalDisclaimerAgree = () => {
    trackCustomEvent(analyticsTags.CaptureGuestPaymentLegalDisclaimerAgreeTags);
    setShowLegalDisclaimer(false);

    sessionStorage.setItem('pncCallbackRole', 'guest');
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const handleLegalDisclaimerClose = () => {
    trackCustomEvent(
      analyticsTags.CaptureGuestPaymentLegalDisclaimerCancelTags,
    );
    setShowLegalDisclaimer(false);
    setPaymentEncryptedData(null);
    setIsLoading(false);
  };

  const onCloseProcessingModal = () => {
    trackCustomEvent(
      analyticsTags.CaptureProcessingTroubleModalCloseGuestPaymentPage,
    );
    setIsProcessingTroublePopupVisible(false);
    handleBeforeUnload();
  };

  const handleSuccessBannerClose = () => {
    setPaymentStatus(undefined);
    // Mark as manually closed so it won't show again even on refresh
    const urlParams = new URLSearchParams(globalThis.location.search);
    const routeParams = props?.route?.params || {};
    const policyNumber =
      urlParams.get('account') || routeParams.account || 'guest';
    // Mark as manually closed instead of just removing
    sessionStorage.setItem(getPaymentStatusKey(policyNumber), 'closed');
  };

  const dateOfBirthErrorList =
    errorList.length > 0
      ? errorList
      : errors.dateOfBirth
        ? [{ errorItem: errors.dateOfBirth }]
        : [];

  return (
    <ScreenWrapper scrollFooter paddingTop paddingBottom paddingHorizontal>
      <View>
        <Components.Text.H1
          className="flex-1 text-sm md:text-lg mb-5"
          style={styles.colorBlue}
        >
          Guest Premium Payment
        </Components.Text.H1>
      </View>
      <LoadingOverlay loading={isLoading} />
      <View className="w-full lg:w-1/2">
        {isPendingBannerVisible && (
          <Components.AlertMessage
            className="mt-4"
            showCloseButton={true}
            onCloseMessage={() => {
              setIsPendingBannerVisible(false);
            }}
            alertLevel={AlertLevel.ALERT}
            iconSize={BreakpointConstants.isLG(width) ? 52.36 : 24}
            title="There is a pending transaction in process"
          >
            <Components.Text style={styles.alertBannerContentMargin}>
              You cannot make any changes until it is complete
            </Components.Text>
          </Components.AlertMessage>
        )}
        {isErrorBannerVisible && (
          <Components.AlertMessage
            className="mt-4"
            showCloseButton={true}
            onCloseMessage={() => {
              setIsErrorBannerVisible(false);
            }}
            alertLevel={AlertLevel.ERROR}
            iconSize={BreakpointConstants.isLG(width) ? 52.36 : 24}
            title="This request could not be processed as submitted."
          >
            <Components.Text style={styles.alertBannerContentMargin}>
              {`Please review the information and try again. If you continue to experience problems, please contact us at `}
              <Components.Link
                onPress={() => {
                  Linking.openURL('tel:800-537-6442');
                }}
                accessibilityLabel="contact number"
                accessibilityRole="button"
                {...phnLinkStyle}
              >
                800-537-6442
              </Components.Link>
              {` for further assistance.`}
            </Components.Text>
          </Components.AlertMessage>
        )}
        {paymentStatus === 'success' && (
          <SuccessBanner
            width={width}
            styles={styles}
            phnLinkStyle={phnLinkStyle}
            onClose={handleSuccessBannerClose}
          />
        )}
        {isIneligibleBannerVisible && (
          <IneligiblePaymentBanner
            width={width}
            styles={styles}
            phnLinkStyle={phnLinkStyle}
            onClose={() => setIsIneligibleBannerVisible(false)}
          />
        )}
        <Components.Card>
          <Components.Text style={styles.alertBannerContentMargin}>
              {`This option is only for billed Policy Premium and billed PUA Payments. To make an unscheduled PUA or loan payment, please go to the`} <Components.Link
                onPress={() => {
                  Linking.openURL(
                    `${AEM_API_BASE_URL.replace(/\/$/, '')}/policy-service-request`,
                  );
                }}
                {...phnLinkStyle}
              >
                Individual Policy Service Request
              </Components.Link>
              {`.`}
            </Components.Text>
            <View style={styles.horizontalDividerTitle} />
          <View style={styles.formContainer}>
            <Components.Text.H2 className="mb-4" style={styles.sectionTitle}>
              Enter Policyholder Information
            </Components.Text.H2>
            <View style={styles.horizontalDivider} />

            {/* Policy Number */}
            <View style={styles.fieldContainer}>
              <Components.TextInput
                label="Policy Number"
                value={formData.policyNumber}
                onChangeText={(value) =>
                  handleInputChange('policyNumber', value)
                }
                placeholder="Enter Policy Number"
                keyboardType="default"
                ErrorList={
                  errors.policyNumber
                    ? [{ errorItem: errors.policyNumber }]
                    : []
                }
                showErrors={!!errors.policyNumber}
              />
            </View>

            {/* Identification Type */}
            <View style={styles.fieldContainer}>
              <Components.Text.H2 style={styles.fieldLabel}>
                Identification Type
              </Components.Text.H2>
              <Components.RadioGroup
                vertical={false}
                //@ts-ignore
                columns={['auto', 'auto']}
                value={formData.identificationType}
                onChange={(val) =>
                  handleInputChange('identificationType', val as string)
                }
                style={styles.radioGroupalignment}
              >
                <Components.RadioButton id="ssn" testID="ssn">
                  Social Security Number
                </Components.RadioButton>
                <Components.RadioButton id="ein" testID="ein">
                  Federal EIN
                </Components.RadioButton>
              </Components.RadioGroup>
            </View>

            {/* SSN or EIN */}
            {formData.identificationType === 'ssn' ? (
              <View style={styles.fieldContainer}>
                <Components.TextInput
                  label="Social Security Number"
                  value={formData.ssn}
                  onChangeText={handleSSNInput}
                  placeholder=""
                  keyboardType="number-pad"
                  inputMode="numeric"
                  maxLength={9}
                  ErrorList={errors.ssn ? [{ errorItem: errors.ssn }] : []}
                  showErrors={!!errors.ssn}
                  secureTextEntry={true}
                />
              </View>
            ) : (
              <View style={styles.fieldContainer}>
                <Components.TextInput
                  label="Federal EIN"
                  value={formData.ein}
                  onChangeText={handleEINInput}
                  placeholder=""
                  keyboardType="number-pad"
                  inputMode="numeric"
                  maxLength={9}
                  ErrorList={errors.ein ? [{ errorItem: errors.ein }] : []}
                  showErrors={!!errors.ein}
                  secureTextEntry={true}
                />
              </View>
            )}

            {/* Date of Birth */}
            <View style={styles.fieldContainer}>
              <Components.DatePicker
                format={DATEFORMAT}
                testID="date-picker"
                label="Date of Birth"
                date={isDateValid(formData.dateOfBirth, DATEFORMAT)}
                placeholder="MM/DD/YYYY"
                maxDate={new Date()}
                onChange={(date) => {
                  setErrorList([]);
                  setErrors({ ...errors, dateOfBirth: '' });
                  onChangeDate(moment(date, DATEFORMAT).format(DATEFORMAT));
                }}
                onBlur={(e) => {
                  onChangeDate(e?.nativeEvent?.text);
                }}
                onChangeText={(value) => {
                  // Pass raw input to validation, no formatting
                  onChangeDate(value);
                }}
                ErrorList={dateOfBirthErrorList}
              />
            </View>

            {/* Email Address */}
            <View style={styles.fieldContainer}>
              <Components.TextInput
                label="Email address"
                value={formData.email}
                onChangeText={(value) => handleInputChange('email', value)}
                placeholder="example@domain.com"
                keyboardType="email-address"
                autoCapitalize="none"
                ErrorList={errors.email ? [{ errorItem: errors.email }] : []}
                showErrors={!!errors.email}
              />
            </View>

            <View style={styles.fieldContainer}>
              <div className="component-captcha">
                <div
                  className="g-recaptcha"
                  data-sitekey={CAPTCHA_KEY}
                  data-badge="inline"
                  data-size="invisible"
                />
              </div>
            </View>

            <View style={styles.buttonContainer}>
              <Components.Button
                style={styles.cancelbutton}
                accessibilityRole="button"
                testID="exitButton"
                theme="secondary"
                onPress={handleExit}
              >
                Back
              </Components.Button>
              <Components.Button
                accessibilityRole="button"
                id="btnOutline"
                testID="captchaSubmit"
                theme="primary"
                onPress={handleSubmit}
                disabled={!isFormComplete()}
              >
                Submit
              </Components.Button>
            </View>
          </View>
        </Components.Card>
      </View>
      <MakePaymentModal
        key={modalResetKey}
        onSubmit={handlePaymentSubmit}
        onCloseAnalytics={sendAnalytics}
        isModalVisible={isModalVisible}
        setModalVisible={setIsModalVisible}
        payments={getPaymentTypes()}
      />
      <form
        ref={formRef}
        action={PNC_SHO_URL}
        method="post"
        style={{ display: 'none' }}
      >
        <input
          type="hidden"
          name="BillTypeLabel"
          value={paymentEncryptedData?.billTypeLabel ?? ''}
        />
        <input
          type="hidden"
          name="CompanyName"
          value={paymentEncryptedData?.companyName ?? ''}
        />
        <input
          type="hidden"
          name="AmountDueLabel"
          value={paymentEncryptedData?.amountDueLabel ?? ''}
        />
        <input
          type="hidden"
          name="EmailAddress"
          value={paymentEncryptedData?.emailAddress ?? ''}
        />
        <input
          type="hidden"
          name="FDICode"
          value={paymentEncryptedData?.fdiCode ?? ''}
        />
        <input
          type="hidden"
          name="PolicyNumber"
          value={paymentEncryptedData?.policyNumber ?? ''}
        />
        <input
          type="hidden"
          name="ClientID"
          value={paymentEncryptedData?.clientID ?? ''}
        />
        <input type="hidden" name="ID" value={paymentEncryptedData?.ID ?? ''} />
      </form>
      <LegalDisclaimerModal
        isVisible={showLegalDisclaimer}
        onAgree={handleLegalDisclaimerAgree}
        onClose={handleLegalDisclaimerClose}
        isHomePage={false}
      />
      <ProcessingTroubleModal
        showModal={isProcessingTroublePopupVisible}
        onClose={onCloseProcessingModal}
      />
    </ScreenWrapper>
  );
};

export default GuestPayment;
